/**
 * Admin Settings - Extended with White-label config
 */
import React, { useState, useEffect } from 'react';
import { Save, Server, Shield, Users, Radio, Palette, Globe, Sliders } from 'lucide-react';
import axios from 'axios';

function Toggle({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-start justify-between py-4"
      style={{ borderBottom: '1px solid #FBFBFD' }}>
      <div>
        <div className="text-sm font-semibold text-n-800">{label}</div>
        {desc && <div className="text-xs text-n-500 mt-0.5">{desc}</div>}
      </div>
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-all duration-200 flex-shrink-0 ml-4 ${checked ? 'bg-s-blue' : 'bg-slate-700'}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${checked ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">{label}</label>
      <input
        className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 outline-none transition-all placeholder-n-400"
        style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
        {...props}
      />
    </div>
  );
}

const PRESET_COLORS = ['#007AFF','#34C759','#FF375F','#FF9500','#C77DFF','#FF9A3C','#4ECDC4'];

function ColorPicker({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">{label}</label>
      <div className="flex items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {PRESET_COLORS.map(c => (
            <button key={c} type="button" onClick={() => onChange(c)}
              className="w-7 h-7 rounded-full transition-all"
              style={{ background: c, outline: value === c ? '2px solid white' : 'none', outlineOffset: '2px' }} />
          ))}
        </div>
        <input type="color" value={value || '#007AFF'}
          onChange={e => onChange(e.target.value)}
          className="w-10 h-8 rounded cursor-pointer border-0 bg-transparent" />
        <span className="text-xs text-n-500 font-mono">{value}</span>
      </div>
    </div>
  );
}

const defaultSettings = {
  appName: 'RocketTalk',
  maxUsersPerChannel: 100,
  allowRegistration: false,
  requireApproval: false,
  'app.name': 'RocketTalk',
  'app.logo': '',
  'app.primaryColor': '#007AFF',
  'app.accentColor': '#34C759',
  'app.companyName': '',
  'app.supportEmail': '',
  'app.maxChannels': '100',
  'app.allowGuestAccess': 'false',
  'app.footerText': '',
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(defaultSettings);
  const [serverInfo, setServerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [settingsRes, healthRes] = await Promise.all([
          axios.get('/api/admin/settings'),
          axios.get('/api/health'),
        ]);
        setSettings({ ...defaultSettings, ...settingsRes.data });
        setServerInfo(healthRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      await axios.put('/api/admin/settings', settings);
      setMsg('Configuración guardada correctamente');
      // Apply CSS var immediately
      if (settings['app.primaryColor']) {
        document.documentElement.style.setProperty('--primary', settings['app.primaryColor']);
      }
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const formatUptime = (secs) => {
    if (!secs) return '—';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    return `${h}h ${m}m`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-n-800">Configuración</h1>
        <p className="text-sm text-n-500 mt-0.5">Ajustes globales y white-label de RocketTalk</p>
      </div>

      {/* Server info */}
      {serverInfo && (
        <div className="rounded-2xl p-5 mb-4" style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Server size={14} className="text-s-blue" />
            <h2 className="font-bold text-n-800 text-sm">Servidor</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-n-500 uppercase tracking-wider mb-1">Estado</div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm font-semibold text-s-green">Operativo</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-n-500 uppercase tracking-wider mb-1">Uptime</div>
              <div className="text-sm font-semibold text-n-800">{formatUptime(serverInfo.uptime)}</div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Branding */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Globe size={14} className="text-s-blue" />
            <h2 className="font-bold text-n-800 text-sm">Branding</h2>
          </div>
          <div className="space-y-4">
            <InputField label="Nombre de la app" value={settings['app.name']}
              onChange={e => set('app.name', e.target.value)} placeholder="RocketTalk" />
            <InputField label="Nombre de empresa" value={settings['app.companyName']}
              onChange={e => set('app.companyName', e.target.value)} placeholder="Mi Empresa S.A." />
            <InputField label="Email de soporte" value={settings['app.supportEmail']}
              onChange={e => set('app.supportEmail', e.target.value)} placeholder="soporte@empresa.com" />
            <InputField label="Texto del footer" value={settings['app.footerText']}
              onChange={e => set('app.footerText', e.target.value)} placeholder="© 2024 Mi Empresa" />
            <InputField label="Logo URL (o base64)" value={settings['app.logo']}
              onChange={e => set('app.logo', e.target.value)} placeholder="https://..." />
          </div>
        </div>

        {/* Colors */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Palette size={14} className="text-s-blue" />
            <h2 className="font-bold text-n-800 text-sm">Colores</h2>
          </div>
          <div className="space-y-4">
            <ColorPicker label="Color primario" value={settings['app.primaryColor']}
              onChange={v => set('app.primaryColor', v)} />
            <ColorPicker label="Color de acento" value={settings['app.accentColor']}
              onChange={v => set('app.accentColor', v)} />
          </div>
        </div>

        {/* Limits */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Sliders size={14} className="text-s-blue" />
            <h2 className="font-bold text-n-800 text-sm">Límites</h2>
          </div>
          <div className="space-y-4">
            <InputField label="Máx. canales" type="number" min="1" max="1000"
              value={settings['app.maxChannels']}
              onChange={e => set('app.maxChannels', e.target.value)} />
            <InputField label="Máx. usuarios por canal (default)" type="number" min="1" max="500"
              value={settings.maxUsersPerChannel}
              onChange={e => set('maxUsersPerChannel', parseInt(e.target.value) || 100)} />
          </div>
        </div>

        {/* Access control */}
        <div className="rounded-2xl px-5" style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-2 pt-5 mb-2">
            <Shield size={14} className="text-s-blue" />
            <h2 className="font-bold text-n-800 text-sm">Control de Acceso</h2>
          </div>
          <Toggle label="Permitir auto-registro"
            desc="Los usuarios pueden registrarse sin invitación."
            checked={settings.allowRegistration}
            onChange={v => set('allowRegistration', v)} />
          <Toggle label="Requiere aprobación de admin"
            desc="Los nuevos registros quedan pendientes hasta aprobación."
            checked={settings.requireApproval}
            onChange={v => set('requireApproval', v)} />
          <Toggle label="Acceso de invitados"
            desc="Permite acceso sin cuenta registrada."
            checked={settings['app.allowGuestAccess'] === 'true' || settings['app.allowGuestAccess'] === true}
            onChange={v => set('app.allowGuestAccess', String(v))} />
          <div className="pb-4" />
        </div>

        {msg && (
          <div className="p-3 rounded-xl text-sm text-s-green bg-green-900/20 border border-green-800/30">{msg}</div>
        )}
        {err && (
          <div className="p-3 rounded-xl text-sm text-s-red bg-red-900/20 border border-red-800/30">{err}</div>
        )}

        <button type="submit" disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-n-800 disabled:opacity-50 transition-all"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          <Save size={14} />
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </form>
    </div>
  );
}
